import React from "react";
import { CONTACT_EMAIL, CONTACT_PHONE } from "../data/mock";
import SEO from "../components/SEO";

const Section = ({ title, children }) => (
  <div className="mt-12">
    <h2 className="font-display font-extrabold text-xl md:text-2xl text-neutral-950 tracking-tight">
      {title}
    </h2>
    <div className="mt-3 text-neutral-700 leading-relaxed space-y-3 text-[15px]">
      {children}
    </div>
  </div>
);

const Sub = ({ title, children }) => (
  <div className="mt-5">
    <h3 className="font-semibold text-neutral-950 text-base">{title}</h3>
    <div className="mt-1.5 space-y-2">{children}</div>
  </div>
);

const DatenschutzPage = () => {
  return (
    <>
      <SEO
        title="Datenschutzerklärung"
        description="Datenschutzerklärung von FREEAYTO Media – Hinweise zur Verarbeitung personenbezogener Daten gemäß DSGVO auf freeayto.de."
        path="/datenschutz"
        keywords="Datenschutz, DSGVO, freeayto, Datenschutzerklärung Marketing Agentur"
      />
      <section className="pt-36 md:pt-44 pb-24 md:pb-32 bg-white">
        <div className="max-w-[820px] mx-auto px-6 md:px-10">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-100 border border-black/5 text-xs uppercase tracking-[0.2em] font-semibold text-neutral-700">
            Rechtliches
          </span>
          <h1
            className="font-display font-bold text-5xl md:text-7xl tracking-[-0.04em] leading-[0.98] mt-6 text-neutral-950"
            data-testid="datenschutz-heading"
          >
            Datenschutz&shy;erklärung
          </h1>
          <p className="mt-4 text-neutral-500 text-sm">
            Zuletzt aktualisiert: Februar 2026 · Gemäß EU-Datenschutz-Grundverordnung (DSGVO) &amp; BDSG.
          </p>

          <Section title="1. Verantwortlicher">
            <p>
              Verantwortlicher im Sinne der DSGVO und anderer nationaler Datenschutzgesetze der Mitgliedstaaten
              sowie sonstiger datenschutzrechtlicher Bestimmungen ist:
            </p>
            <p className="pt-1">
              <b>Keanu Irle · FREEAYTO Media</b>
              <br />
              Erich Kästner Ring 23<br />
              44339 Dortmund<br />
              Deutschland
            </p>
            <p>
              E-Mail:{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-pink-500">
                {CONTACT_EMAIL}
              </a>
              <br />
              Telefon:{" "}
              <a
                href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}
                className="underline hover:text-pink-500"
              >
                {CONTACT_PHONE}
              </a>
            </p>
            <p>USt-ID: DE461970096</p>
          </Section>

          <Section title="2. Allgemeine Hinweise zur Datenverarbeitung">
            <p>
              Wir verarbeiten personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit dies zur
              Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen erforderlich ist.
              Die Verarbeitung erfolgt regelmäßig nur nach Einwilligung des Nutzers oder wenn eine gesetzliche
              Grundlage die Verarbeitung gestattet.
            </p>
            <p>
              <b>Rechtsgrundlagen:</b> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), lit. b (Vertrag/vorvertragliche
              Maßnahmen), lit. c (rechtliche Verpflichtung) und lit. f DSGVO (berechtigtes Interesse).
            </p>
          </Section>

          <Section title="3. Hosting &amp; Bereitstellung der Website">
            <Sub title="Emergent (Cloud-Hosting-Plattform)">
              <p>
                Diese Website wird technisch von <b>Emergent Labs</b> gehostet – einer Managed-Cloud-Plattform,
                die die Auslieferung, den Betrieb sowie das automatische Deployment übernimmt. Beim Aufruf der
                Website werden dabei technisch notwendige Verbindungsdaten (u. a. IP-Adresse, Zeitstempel, User-Agent,
                aufgerufene URL, HTTP-Statuscode) automatisch verarbeitet.
              </p>
              <p>
                Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer stabilen,
                sicheren und funktional zuverlässigen Bereitstellung unseres Online-Angebots).
              </p>
              <p>
                Sofern Serverstandorte außerhalb der EU/des EWR liegen, erfolgt eine Übermittlung
                personenbezogener Daten in Drittländer ausschließlich auf Grundlage der EU-Standardvertragsklauseln
                (Art. 46 DSGVO) bzw. eines Angemessenheitsbeschlusses gem. Art. 45 DSGVO.
              </p>
            </Sub>
            <Sub title="Server-Logfiles">
              <p>
                Der Provider erhebt automatisch Informationen in Server-Log-Dateien, die der Browser
                übermittelt. Erfasst werden u. a. Browsertyp und -version, verwendetes Betriebssystem,
                Referrer-URL, Hostname, Uhrzeit der Serveranfrage und IP-Adresse. Diese Daten werden
                nach spätestens 30 Tagen automatisiert gelöscht, sofern nicht zur Aufklärung eines
                konkreten Missbrauchsfalls länger gespeichert.
              </p>
            </Sub>
          </Section>

          <Section title="4. Datenbank &amp; interne Datenspeicherung">
            <p>
              Zur Speicherung strukturierter Daten (z. B. Kontaktanfragen, Newsletter-Anmeldungen,
              interne Verwaltung von Inhalten) setzen wir eine <b>MongoDB-Datenbank</b> ein, die
              innerhalb unserer Cloud-Infrastruktur betrieben wird. Zugriff auf diese Daten haben ausschließlich
              autorisierte Personen unseres Teams.
            </p>
          </Section>

          <Section title="5. E-Mail-Versand über IONOS">
            <p>
              Für den Versand transaktionaler E-Mails (Bestätigungen, Antworten auf Anfragen,
              Newsletter-Broadcasts) nutzen wir den E-Mail-Service der{" "}
              <b>1&amp;1 IONOS SE</b>, Elgendorfer Straße 57, 56410 Montabaur (Deutschland).
              Die Datenverarbeitung erfolgt auf Servern in der EU.
            </p>
            <p>
              Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) bzw. lit. f DSGVO
              (berechtigtes Interesse an einer zuverlässigen Kommunikation).
            </p>
          </Section>

          <Section title="6. Kontaktformular &amp; Anfragen">
            <p>
              Bei Nutzung unseres Kontaktformulars werden die von dir eingegebenen Daten
              (Name, E-Mail, Telefon, Unternehmen, gewünschte Leistung, Budget, Nachricht) zur
              Bearbeitung deiner Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
            </p>
            <p>
              Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen) sowie lit. f DSGVO
              (Bearbeitung einer Kontaktanfrage). Die Daten werden gelöscht, sobald sie für die Zweckerreichung
              nicht mehr erforderlich sind – spätestens nach Ablauf gesetzlicher Aufbewahrungsfristen.
            </p>
          </Section>

          <Section title="7. Newsletter">
            <p>
              Für den Versand unseres Newsletters benötigen wir deine E-Mail-Adresse sowie deine Einwilligung,
              dass du mit dem Bezug des Newsletters einverstanden bist. Wenn du dich über das Kontaktformular
              meldest, wirst du optional automatisch auch für den Newsletter angemeldet. Deine Einwilligung
              kannst du <b>jederzeit widerrufen</b>, ohne dass die Rechtmäßigkeit der aufgrund der Einwilligung
              bis zum Widerruf erfolgten Verarbeitung berührt wird.
            </p>
            <p>
              Der Widerruf ist per formloser E-Mail an{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-pink-500">
                {CONTACT_EMAIL}
              </a>{" "}
              oder über den Abmelde-Link jeder Newsletter-Mail möglich.
              Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO.
            </p>
          </Section>

          <Section title="8. Cookies &amp; lokaler Speicher">
            <p>
              Unsere Website verwendet Cookies bzw. Local-Storage-Einträge. Dabei handelt es sich um kleine
              Textdateien, die auf deinem Endgerät gespeichert werden. Wir unterscheiden zwischen:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <b>Technisch notwendige Speicher-Einträge</b> (z. B. dein Cookie-Consent-Status,
                Session-Handling im Admin-Bereich) – Rechtsgrundlage: § 25 Abs. 2 TDDDG bzw. Art. 6 Abs. 1 lit. f DSGVO.
              </li>
              <li>
                <b>Optionale Analyse-/Marketing-Einträge</b> – werden ausschließlich nach deiner ausdrücklichen
                Einwilligung im Cookie-Banner gesetzt (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG).
              </li>
            </ul>
            <p>
              Du kannst deine Einwilligung jederzeit widerrufen, indem du die entsprechenden Speicher-Einträge
              in deinem Browser löschst.
            </p>
          </Section>

          <Section title="9. Analyse: Google Analytics 4 (optional, nur mit Einwilligung)">
            <p>
              Sofern du im Cookie-Banner <i>„Alle akzeptieren"</i> ausgewählt hast, verwenden wir{" "}
              <b>Google Analytics 4</b>, einen Web-Analysedienst der Google Ireland Ltd., Gordon House,
              Barrow Street, Dublin 4, Irland. Verarbeitet werden u. a. gekürzte IP-Adresse (IP-Anonymisierung
              ist aktiviert), Geräte- und Browser-Informationen, aufgerufene Seiten sowie das Nutzungsverhalten.
            </p>
            <p>
              Die Nutzung erfolgt zur bedarfsgerechten Gestaltung und fortlaufenden Optimierung unserer
              Website. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO. Eine Datenübermittlung in die USA kann
              erfolgen – abgesichert über EU-Standardvertragsklauseln und das EU-US Data Privacy Framework.
              Weitere Informationen:{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-pink-500"
              >
                policies.google.com/privacy
              </a>
              .
            </p>
          </Section>

          <Section title="10. Eingebettete Inhalte &amp; externe Medien">
            <Sub title="Social-Media-Embeds (Instagram, TikTok, YouTube)">
              <p>
                Auf einigen Unterseiten (insb. <i>/erfolge</i>) betten wir Inhalte aus Instagram, TikTok
                und YouTube ein. Sobald du eine Seite mit einem solchen Embed aufrufst, wird eine
                Direktverbindung zu Servern der jeweiligen Plattform aufgebaut. Der Anbieter erfährt
                dadurch, dass deine IP-Adresse unsere Seite aufgerufen hat. Sind Nutzer:innen dort eingeloggt,
                können weitere Daten dem jeweiligen Konto zugeordnet werden.
              </p>
              <p>
                Anbieter:{" "}
                <br />
                Meta Platforms Ireland Ltd. (Instagram), Merrion Road, Dublin 4, Irland ·{" "}
                <a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noreferrer" className="underline">
                  Datenschutz
                </a>
                <br />
                TikTok Technology Ltd., 10 Earlsfort Terrace, Dublin 2, Irland ·{" "}
                <a href="https://www.tiktok.com/legal/privacy-policy-eea" target="_blank" rel="noreferrer" className="underline">
                  Datenschutz
                </a>
                <br />
                Google Ireland Ltd. (YouTube), Gordon House, Barrow Street, Dublin 4, Irland ·{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">
                  Datenschutz
                </a>
              </p>
            </Sub>
            <Sub title="Google Fonts &amp; Bild-CDNs (Unsplash)">
              <p>
                Zur einheitlichen Darstellung von Schriftarten und Grafiken laden wir Assets teilweise
                von externen Anbietern (Google Fonts, Unsplash). Beim Aufruf wird die IP-Adresse an
                deren Server übertragen. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (einheitliche
                Darstellung des Online-Angebots).
              </p>
            </Sub>
          </Section>

          <Section title="11. Deine Rechte als betroffene Person">
            <p>Du hast gegenüber uns folgende Rechte hinsichtlich der dich betreffenden personenbezogenen Daten:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
              <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
              <li>Recht auf Löschung – „Recht auf Vergessenwerden" (Art. 17 DSGVO)</li>
              <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Recht auf Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
              <li>Recht auf Widerruf einer Einwilligung (Art. 7 Abs. 3 DSGVO)</li>
              <li>
                Recht auf Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO) – z. B. bei der
                Landesbeauftragten für Datenschutz und Informationsfreiheit NRW.
              </li>
            </ul>
            <p>
              Zur Ausübung deiner Rechte genügt eine formlose Mitteilung per E-Mail an{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-pink-500">
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section title="12. SSL-/TLS-Verschlüsselung">
            <p>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher
              Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennst du
              am „https://" in der Adresszeile und am Schloss-Symbol.
            </p>
          </Section>

          <Section title="13. Änderungen dieser Datenschutzerklärung">
            <p>
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, wenn sich rechtliche Rahmenbedingungen
              ändern oder bei Änderungen des Dienstes bzw. der Datenverarbeitung. Es gilt jeweils die aktuelle,
              hier abrufbare Version.
            </p>
          </Section>
        </div>
      </section>
    </>
  );
};

export default DatenschutzPage;
